import { type FetchResult, InMemoryCache } from '@apollo/client';
import { ApolloClient } from 'apollo-client';

import { getIetfLanguageTag } from 'js/lib/language';

import type {
  ChatMessagesMessageFragment,
  AiCoachPlatform_ChatMessagesPaginatedResultQuery as ChatMessagesQuery,
  AiCoachPlatform_ChatMessagesPaginatedResultQueryVariables as ChatMessagesVariables,
  ProcessingSuccessFragment,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachChatMessagesByChatSessionId';
import type {
  AiCoachPlatform_RespondToUserMessageMutation as RespondToUserMessageMutation,
  AiCoachPlatform_RespondToUserMessageMutationVariables as RespondToUserMessageVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachRespondToUserMessageMutation';
import aiCoachChatMessagesByChatSessionId from 'bundles/ai-coach-platform/queries/aiCoachChatMessagesByChatSessionId.graphql';
import aiCoachRespondToUserMessageMutation from 'bundles/ai-coach-platform/queries/aiCoachRespondToUserMessageMutation.graphql';

const MAXIMUM_WAIT_MS = 1000 * 60 * 5;
const POLL_INTERVAL_MS = 1000 * 2;

const hasExceededMaximumWaitTime = (iteration: number) => iteration * POLL_INTERVAL_MS > MAXIMUM_WAIT_MS;

/** A single return type for both code paths to ensure downstream steps are consistent */
export type UnifiedReturnType = FetchResult<RespondToUserMessageMutation>;

const wait = () => new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

const client = (operationName: string) => {
  const cache = new InMemoryCache();
  return new ApolloClient({
    uri: `/graphql-gateway?opname=${operationName}`,
    cache,
    headers: {
      'Accept-Language': getIetfLanguageTag(),
    },
  });
};

const callMessageCreationMutation = (variables: RespondToUserMessageVariables): Promise<UnifiedReturnType> => {
  const result = client('AiCoach_RespondToUserMessage').mutate<RespondToUserMessageMutation>({
    mutation: aiCoachRespondToUserMessageMutation,
    variables,
    context: { clientName: 'gatewayGql' },
  });
  return result;
};

const queryLastMessageInCurrentChat = (chatSessionId: string) =>
  client('AiCoachPlatform_ChatMessagesPaginatedResult').query<ChatMessagesQuery, ChatMessagesVariables>({
    query: aiCoachChatMessagesByChatSessionId,
    variables: {
      limit: 1,
      chatSessionId,
    },
    context: { clientName: 'gatewayGql' },
  });

const waitForMessageToComplete = async (
  chatSessionId: string,
  iteration: number
): Promise<ChatMessagesMessageFragment> => {
  if (hasExceededMaximumWaitTime(iteration)) {
    throw new Error('Exceeded the max number of attempts while waiting for a Coach task-based message to complete');
  }
  await wait();
  const result = await queryLastMessageInCurrentChat(chatSessionId);

  const mostRecentMessage = (result.data.AiCoach?.queryByChatSessionId.elements || [])?.[0];

  const taskStatus = mostRecentMessage?.taskStatus;
  if (!taskStatus) {
    throw new Error('Invalid data returned from AiCoachPlatform_ChatMessagesPaginatedResult');
  }

  // Case 1 - it's not yet done
  if (!taskStatus.taskProcessingResult) {
    return waitForMessageToComplete(chatSessionId, iteration + 1);
  }

  // Case 2 - it's completed successfully; return the most recent message
  if ((taskStatus.taskProcessingResult as ProcessingSuccessFragment)?.taskProcessingCompletedAt) {
    return mostRecentMessage;
  }

  // Case 3 - it's failed
  throw new Error('Task-based processing message failed');
};

interface NormalizeResponseArgs {
  message: ChatMessagesMessageFragment;
  chatSessionId: string;
}
/** Transform the record returned from AiCoachPlatform_ChatMessagesPaginatedResult so that it adheres to the same structure as data returned from AiCoach_RespondToUserMessage */
const normalizeResponse = ({ message, chatSessionId }: NormalizeResponseArgs): UnifiedReturnType => ({
  data: {
    AiCoach_RespondToUserMessage: {
      chatSessionId,
      messageByAiCoach: {
        message: message.message,
        messageId: message.id,
        senderType: message.senderType,
        structuredResponse: message.structuredResponse,
      },
      recommendedActions: message.recommendedActionsV2 || [],
      navigationButtons: message.navigationButtonsV2,
      recommendedEdits: message.recommendedEdits,
    },
  },
});

const callAiCoachRespondToUserMessage = async (
  variables: RespondToUserMessageVariables
): Promise<UnifiedReturnType> => {
  const initialCall = await callMessageCreationMutation(variables);

  if (variables.input.responseProcessingType !== 'TASK_BASED_PROCESSING') {
    return initialCall;
  }

  const initialCallData = initialCall.data;

  if (!initialCallData) {
    throw new Error('No data returned from AiCoach_RespondToUserMessage');
  }

  const chatSessionId = initialCallData.AiCoach_RespondToUserMessage.chatSessionId;

  const message = await waitForMessageToComplete(chatSessionId, 1);

  return normalizeResponse({ message, chatSessionId });
};

export default callAiCoachRespondToUserMessage;
